import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const loadCart = useCallback(async () => {
    if (!user) { setCart([]); return; }
    try { const { data } = await api.get("/cart"); setCart(data); }
    catch (e) { if (process.env.NODE_ENV === "development") console.error("Failed to load cart:", e); }
  }, [user]);

  const loadWishlist = useCallback(async () => {
    if (!user) { setWishlist([]); return; }
    try { const { data } = await api.get("/wishlist"); setWishlist(data); }
    catch (e) { if (process.env.NODE_ENV === "development") console.error("Failed to load wishlist:", e); }
  }, [user]);

  useEffect(() => { loadCart(); loadWishlist(); }, [loadCart, loadWishlist]);

  const addToCart = useCallback(async (product_id, quantity = 1) => {
    const { data } = await api.post("/cart", { product_id, quantity });
    setCart(data);
  }, []);
  const updateCart = useCallback(async (product_id, quantity) => {
    const { data } = await api.put(`/cart/${product_id}`, { product_id, quantity });
    setCart(data);
  }, []);
  const removeCart = useCallback(async (product_id) => {
    const { data } = await api.delete(`/cart/${product_id}`);
    setCart(data);
  }, []);
  const toggleWishlist = useCallback(async (product_id) => {
    await api.post(`/wishlist/${product_id}`);
    loadWishlist();
  }, [loadWishlist]);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const wishlistIds = wishlist.map((w) => w.product_id);

  const value = useMemo(
    () => ({ cart, wishlist, wishlistIds, cartCount, addToCart, updateCart, removeCart, toggleWishlist, loadCart }),
    [cart, wishlist, wishlistIds, cartCount, addToCart, updateCart, removeCart, toggleWishlist, loadCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
